/* Slides tool — prompt-driven lecture deck builder.
 *
 * Generate from prompt OR upload PDF/Word. Library of saved decks. Live prompt-edit
 * (theme change, slide add, rewording). Fullscreen Present mode.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Loader2, Wand2, Trash2, Upload, FileText, ChevronLeft, ChevronRight, Play, Sparkles, Edit3, Send,
  Image as ImageIcon,
} from "lucide-react";

export const THEMES = {
  indigo: { bg: "from-[#312E81] via-[#3730A3] to-[#1E1B4B]", text: "text-white", accent: "#FFD600" },
  mint:   { bg: "from-[#064E3B] via-[#047857] to-[#022C22]", text: "text-white", accent: "#FBBF24" },
  sunset: { bg: "from-[#9A3412] via-[#C2410C] to-[#7C2D12]", text: "text-white", accent: "#FCD34D" },
  dark:   { bg: "from-[#0A0C10] via-[#10141C] to-[#1A0F2E]", text: "text-white", accent: "#FFD600" },
  paper:  { bg: "from-[#FBFAF7] via-[#F5F4ED] to-[#EAE7DC]", text: "text-[#0A0C10]", accent: "#7C3AED" },
  neon:   { bg: "from-[#0F172A] via-[#1E293B] to-[#020617]", text: "text-white", accent: "#22D3EE" },
};
export const themeFor = (k) => THEMES[k] || THEMES.indigo;

export default function SlidesBody({ chapter, subject }) {
  const [decks, setDecks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [topic, setTopic] = useState("");
  const [prompt, setPrompt] = useState("");
  const [theme, setTheme] = useState("indigo");
  const fileRef = useRef(null);

  const reload = async () => {
    try { const { data } = await api.get("/ai/slides", { params: { chapter_id: chapter.id } }); setDecks(data.decks || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [chapter.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/slides/generate", {
        topic: topic || chapter.title, prompt,
        subject_id: subject.id, chapter_id: chapter.id, n: 8, theme,
      });
      toast.success(`${data.deck.slides.length} slides ready`);
      setPrompt(""); setTopic("");
      reload();
      setOpen(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("topic", topic || f.name);
    fd.append("prompt", prompt || "");
    fd.append("n", "10"); fd.append("theme", theme);
    fd.append("subject_id", subject.id); fd.append("chapter_id", chapter.id);
    try {
      const { data } = await api.post("/ai/slides/upload", fd);
      toast.success(`Imported ${f.name} → ${data.deck.slides.length} slides`);
      reload(); setOpen(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!confirm("Delete this deck?")) return;
    try { await api.delete(`/ai/slides/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="slides-body">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color: "#0891B2" }}>Slides</div>
      <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">AI Lecture Slides</h2>
      <p className="text-sm text-neutral-500 mt-1">Build a polished deck from a prompt, or upload a PDF/Word and let Mobi reformat it. Edit slides live with plain English.</p>

      {/* Generate form */}
      <div className="mt-5 bg-white border themed-border rounded-md p-4 max-w-3xl space-y-3">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={`Topic (e.g. ${chapter.title})`} data-testid="slides-topic"
          className="w-full px-3 py-2 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10]" />
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="What should this deck cover? (e.g. 'introduce the Italian city-states, 8 slides, kid-friendly language, with a quiz slide at the end')" data-testid="slides-prompt"
          className="w-full px-3 py-2 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10]" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Theme</div>
          <div className="flex flex-wrap gap-1.5" data-testid="slides-theme-picker">
            {Object.keys(THEMES).map((k) => {
              const t = THEMES[k];
              const active = theme === k;
              return (
                <button key={k} type="button" onClick={() => setTheme(k)}
                  data-testid={`slides-theme-${k}`}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium capitalize transition-all bg-gradient-to-r ${t.bg} ${t.text} ${active ? "ring-2 ring-offset-2 ring-[#0A0C10]" : "opacity-70 hover:opacity-100"}`}
                  style={{ borderColor: t.accent }}>
                  {k}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={generate} disabled={busy} data-testid="slides-generate"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0891B2] text-white text-sm font-medium hover:bg-[#0E7490] disabled:opacity-50">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Wand2 size={14} /> Generate deck</>}
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="slides-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border themed-border text-sm font-medium hover:bg-black/5 disabled:opacity-50">
            <Upload size={14} /> Import PDF / DOCX
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.docx" hidden onChange={(e) => upload(e.target.files?.[0])} />
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">12 credits</span>
        </div>
      </div>

      {/* Saved decks */}
      {decks.length > 0 && (
        <div className="mt-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">Saved decks · {decks.length}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {decks.map((d) => (
              <div key={d.id} className={`bg-gradient-to-br ${themeFor(d.theme).bg} ${themeFor(d.theme).text} border rounded-md p-3 group`} style={{ borderColor: themeFor(d.theme).accent + "55" }}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{(d.slides || []).length} slides · {d.source}</div>
                <div className="font-display font-bold text-sm tracking-tighter mt-1 line-clamp-2">{d.title}</div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => setOpen(d)} data-testid={`slides-open-${d.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium">
                    <Play size={11} /> Present
                  </button>
                  <button onClick={() => remove(d.id)} className="text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SlidesPresenter deck={open} onClose={() => setOpen(null)} onUpdated={(d) => { setOpen(d); reload(); }} />
    </div>
  );
}

/* ───────────────────── presenter ───────────────────── */
// Exported so a standalone route can reuse the same fullscreen presenter
// without depending on the SlidesBody chapter-scoped list.
export function SlidesPresenter({ deck, onClose, onUpdated, onIndexChange, syncIndex }) {
  const [idx, setIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const imgRef = React.useRef(null);

  useEffect(() => { if (deck) setIdx(0); }, [deck?.id]);
  // Broadcast slide navigation to the live class (when presenting in-class)
  useEffect(() => { onIndexChange?.(idx); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [idx]);
  // Follow externally-driven navigation (remote-access student controller)
  useEffect(() => {
    if (syncIndex != null && syncIndex !== idx) setIdx(syncIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncIndex]);

  if (!deck) return null;
  const slides = deck.slides || [];
  const slide = slides[idx] || {};
  const th = themeFor(deck.theme);

  const submitEdit = async () => {
    if (!instr.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/slides/${deck.id}/edit`, { instruction: instr });
      toast.success("Updated");
      setInstr(""); setEditing(false);
      onUpdated(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Edit failed"); }
    finally { setBusy(false); }
  };

  const setTheme = async (newTheme) => {
    if (newTheme === deck.theme) return;
    try {
      const { data } = await api.patch(`/ai/slides/${deck.id}`, { theme: newTheme });
      onUpdated(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Theme change failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      // Convert to data URL — small illustrations only, this deck is JSON in Mongo.
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await api.patch(`/ai/slides/${deck.id}`, {
        slide: { index: idx, image_url: dataUrl },
      });
      onUpdated(data.deck);
      toast.success("Image added to slide");
    } catch (e) { toast.error(e.response?.data?.detail || "Image upload failed"); }
    finally { setBusy(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const removeImage = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/ai/slides/${deck.id}`, {
        slide: { index: idx, image_url: "" },
      });
      onUpdated(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setShape = async (shape) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/ai/slides/${deck.id}`, {
        slide: { index: idx, shape: shape === slide.shape ? "" : shape },
      });
      onUpdated(data.deck);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`fixed inset-0 z-[80] bg-gradient-to-br ${th.bg} ${th.text} flex flex-col`} data-testid="slides-presenter">
      <div className="px-5 lg:px-8 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <div className="min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] opacity-70" style={{ color: th.accent }}>/ slides</div>
            <h2 className="font-display font-bold text-xl tracking-tighter truncate">{deck.title}</h2>
          </div>
          <button onClick={() => setEditing((e) => !e)} data-testid="slides-edit-toggle"
            className={`shrink-0 px-3.5 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${editing ? "ring-2 ring-white/70" : ""}`}
            style={{ background: th.accent, color: "#0A0C10" }}>
            <Edit3 size={12} /> Edit with prompt
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} data-testid="slides-close" className="w-9 h-9 rounded-md bg-white/15 hover:bg-white/25 inline-flex items-center justify-center"><X size={16} /></button>
        </div>
      </div>

      {/* PPT-style toolbox strip — theme swap, insert image, add shapes */}
      <div className="px-5 lg:px-8 pb-3 flex flex-wrap items-center gap-2" data-testid="slides-toolbox">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-60 mr-1">Theme</div>
        <div className="flex items-center gap-1">
          {Object.keys(THEMES).map((k) => {
            const t = THEMES[k];
            const active = deck.theme === k;
            return (
              <button key={k} onClick={() => setTheme(k)} data-testid={`presenter-theme-${k}`}
                title={k}
                className={`px-2 py-1 rounded-sm text-[10px] font-medium capitalize bg-gradient-to-r ${t.bg} ${t.text} ${active ? "ring-2 ring-white/80" : "opacity-70 hover:opacity-100"}`}
                style={{ borderColor: t.accent }}>
                {k}
              </button>
            );
          })}
        </div>
        <div className="w-px h-5 bg-white/20 mx-1" />
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-60 mr-1">Insert</div>
        <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="presenter-image-input" />
        <button onClick={() => imgRef.current?.click()} disabled={busy} data-testid="presenter-add-image"
          className="px-2 py-1 rounded-sm bg-white/15 hover:bg-white/25 text-[10px] font-medium inline-flex items-center gap-1">
          <ImageIcon size={11} /> Image
        </button>
        {slide.image_url && (
          <button onClick={removeImage} data-testid="presenter-remove-image"
            className="px-2 py-1 rounded-sm bg-red-500/25 hover:bg-red-500/40 text-[10px] font-medium">
            Remove image
          </button>
        )}
        <div className="w-px h-5 bg-white/20 mx-1" />
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-60 mr-1">Shape</div>
        {[
          { id: "arrow",  label: "Arrow" },
          { id: "dot",    label: "Dot pattern" },
          { id: "corner", label: "Corner brace" },
          { id: "bar",    label: "Side bar" },
        ].map((s) => (
          <button key={s.id} onClick={() => setShape(s.id)} data-testid={`presenter-shape-${s.id}`}
            className={`px-2 py-1 rounded-sm text-[10px] font-medium ${slide.shape === s.id ? "bg-[#FFD600] text-[#0A0C10]" : "bg-white/15 hover:bg-white/25"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {editing && (
        <div className="px-5 lg:px-8 pb-3">
          <div className="bg-white/10 backdrop-blur rounded-md p-3 flex items-center gap-2 max-w-3xl">
            <input value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="e.g. 'change theme to dark', 'add a slide on causes', 'rewrite slide 3 simpler'"
              data-testid="slides-instr" className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40" />
            <button onClick={submitEdit} disabled={busy || !instr.trim()} data-testid="slides-apply"
              className="px-4 py-2 rounded-md font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: th.accent, color: "#0A0C10" }}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} /> Apply</>}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}
            className="relative w-full max-w-5xl aspect-[16/9] bg-black/20 backdrop-blur-sm rounded-2xl p-10 lg:p-16 flex flex-col justify-center border overflow-hidden" style={{ borderColor: th.accent + "33" }}>
            {/* Decorative shape overlays — added from the toolbox */}
            {slide.shape === "dot" && (
              <div className="absolute inset-0 pointer-events-none opacity-30" style={{
                backgroundImage: `radial-gradient(${th.accent} 1px, transparent 1.5px)`, backgroundSize: "22px 22px",
              }} />
            )}
            {slide.shape === "arrow" && (
              <svg className="absolute right-6 bottom-6 pointer-events-none" width="140" height="80" viewBox="0 0 140 80" fill="none">
                <path d="M10 40 L 120 40 M 100 20 L 120 40 L 100 60" stroke={th.accent} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
              </svg>
            )}
            {slide.shape === "corner" && (
              <>
                <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 pointer-events-none" style={{ borderColor: th.accent }} />
                <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 pointer-events-none" style={{ borderColor: th.accent }} />
              </>
            )}
            {slide.shape === "bar" && (
              <div className="absolute left-0 top-0 bottom-0 w-2 pointer-events-none" style={{ background: th.accent }} />
            )}

            <div className="text-[11px] font-mono uppercase tracking-[0.28em] font-bold mb-4 relative z-10" style={{ color: th.accent }}>
              Slide {idx + 1} / {slides.length}
            </div>
            <div className="relative z-10 grid gap-8 items-start" style={{ gridTemplateColumns: slide.image_url ? "1.4fr 1fr" : "1fr" }}>
              <div>
                <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tighter leading-[1.05]">{slide.title}</h2>
                {(slide.body || []).length > 0 && (
                  <ul className="mt-6 space-y-3 max-w-3xl">
                    {(slide.body || []).map((b, i) => (
                      <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.07 }}
                        className="flex items-start gap-3 text-base lg:text-lg">
                        <Sparkles size={14} className="mt-2 shrink-0" style={{ color: th.accent }} />
                        <span>{b}</span>
                      </motion.li>
                    ))}
                  </ul>
                )}
                {slide.notes && (
                  <div className="mt-6 text-xs opacity-50 italic">Speaker notes: {slide.notes}</div>
                )}
              </div>
              {slide.image_url && (
                <div className="rounded-md overflow-hidden border" style={{ borderColor: th.accent + "55" }} data-testid="slide-image">
                  <img src={slide.image_url} alt={slide.title} className="w-full h-auto object-contain bg-black/30" />
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-5 lg:px-8 py-4 flex items-center justify-between">
        <button onClick={() => idx > 0 && setIdx(idx - 1)} disabled={idx === 0} data-testid="slides-prev"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/15 hover:bg-white/25 text-sm disabled:opacity-30">
          <ChevronLeft size={14} /> Previous
        </button>
        <div className="text-xs font-mono opacity-60">← → keys to navigate</div>
        <button onClick={() => idx < slides.length - 1 && setIdx(idx + 1)} disabled={idx >= slides.length - 1} data-testid="slides-next"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-md font-semibold text-sm disabled:opacity-30"
          style={{ background: th.accent, color: "#0A0C10" }}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
